import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Alert, CircularProgress, Typography } from '@mui/material';
import Video from 'twilio-video';
import { TWILIO_VIDEO_REGION } from '../../config';
import { attachVideoTrackInContainer } from '../../utils/twilioVideoDom';
import { twilioViewerBandwidthProfile } from '../../utils/twilioVideoConnectOptions';

const LOG = '[PetLive][TwilioVideo][Viewer]';

/**
 * Espectador: não publica áudio/vídeo; exibe o vídeo remoto e a contagem de participantes.
 * O `name` em Video.connect deve ser o UniqueName da sala (GUID), igual ao VideoGrant no token.
 */
export const TwilioVideoViewer = ({ getToken }) => {
    const remoteRef = useRef(null);
    const roomRef = useRef(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [participantCount, setParticipantCount] = useState(0);

    const updateCount = useCallback((room) => {
        if (!room) {
            return;
        }
        setParticipantCount(room.participants.size + 1);
    }, []);

    const attachRemoteVideo = useCallback((track) => {
        attachVideoTrackInContainer(remoteRef.current, track, LOG, {
            controls: true,
            muted: true
        });
    }, []);

    const subscribeParticipant = useCallback(
        (participant) => {
            const onVideo = (track) => {
                if (track && track.kind === 'video') {
                    attachRemoteVideo(track);
                }
            };
            participant.on('trackSubscribed', onVideo);
            participant.videoTracks.forEach((publication) => {
                if (publication.track) {
                    onVideo(publication.track);
                }
                publication.on('subscribed', onVideo);
            });
        },
        [attachRemoteVideo]
    );

    useEffect(() => {
        let cancelled = false;

        const run = async () => {
            setLoading(true);
            setError('');
            try {
                const result = await getToken();
                if (cancelled) {
                    return;
                }
                if (!result.success || !result.data?.token) {
                    setError(result.message || 'Não foi possível entrar na sala');
                    setLoading(false);
                    return;
                }

                const roomName = result.data.roomName;
                if (!roomName || typeof roomName !== 'string') {
                    console.error(LOG, 'API não retornou roomName; Video.connect exige o mesmo UniqueName do token.');
                    setError('Configuração da sala inválida (roomName).');
                    setLoading(false);
                    return;
                }

                const connectOpts = {
                    name: roomName,
                    audio: false,
                    video: false,
                    bandwidthProfile: twilioViewerBandwidthProfile,
                    preferredVideoCodecs: [{ codec: 'VP8' }]
                };
                if (TWILIO_VIDEO_REGION) {
                    connectOpts.region = TWILIO_VIDEO_REGION;
                }

                console.info(LOG, 'Video.connect', { roomName, region: connectOpts.region || 'default' });
                const room = await Video.connect(result.data.token, connectOpts);
                console.info(LOG, 'conectado', { roomSid: room?.sid, roomName: room?.name });

                if (cancelled) {
                    room.disconnect();
                    return;
                }
                roomRef.current = room;
                room.participants.forEach(subscribeParticipant);
                room.on('participantConnected', (p) => {
                    subscribeParticipant(p);
                    updateCount(room);
                });
                room.on('participantDisconnected', () => updateCount(room));
                updateCount(room);
            } catch (e) {
                console.error(LOG, 'falha', { code: e?.code, message: e?.message });
                if (!cancelled) {
                    if (e && e.code === 20151) {
                        setError(
                            'Autorização do vídeo recusada (20151). No Twilio Console, crie ou use uma API Key na região United States (US1); chaves de outras regiões não servem para Programmable Video. Confira também se TWILIO_API_KEY_SECRET no .env corresponde exatamente ao secret dessa chave (SK).'
                        );
                    } else {
                        setError(e.message || 'Falha ao conectar ao vídeo');
                    }
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        run();

        return () => {
            cancelled = true;
            if (roomRef.current) {
                roomRef.current.disconnect();
                roomRef.current = null;
            }
            if (remoteRef.current) {
                remoteRef.current.innerHTML = '';
            }
        };
    }, [getToken, subscribeParticipant, updateCount]);

    return (
        <Box>
            {loading && (
                <Box display="flex" justifyContent="center" py={3}>
                    <CircularProgress />
                </Box>
            )}
            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}
            <Box
                ref={remoteRef}
                sx={{
                    width: '100%',
                    maxWidth: 960,
                    margin: '0 auto',
                    borderRadius: 2,
                    overflow: 'hidden',
                    bgcolor: '#000',
                    minHeight: 240,
                    '& video': {
                        width: '100%',
                        display: 'block',
                        verticalAlign: 'middle',
                        objectFit: 'contain',
                        maxHeight: '70vh'
                    }
                }}
            />
            {!loading && !error && (
                <>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1, textAlign: 'center' }}>
                        Use os controlos do vídeo (reproduzir, som, ecrã inteiro) se a imagem não arrancar sozinha.
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, textAlign: 'center' }}>
                        {participantCount} {participantCount === 1 ? 'pessoa na sala' : 'pessoas na sala'}
                    </Typography>
                </>
            )}
        </Box>
    );
};
